import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EnquiryForm from '@/components/EnquiryForm';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MapPin,
  Maximize2,
  Building2,
  Phone,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const showEnquiry = searchParams.get('enquiry') === 'true';

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [enquiryType, setEnquiryType] = useState('general');

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const formatPrice = (price) => {
    if (!price) return null;
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const formatArea = (area) => {
    return `${area?.toLocaleString()} sq.ft.`;
  };

  const getPropertyTypeLabel = (type) => {
    const labels = {
      office: 'Office Space',
      retail: 'Retail Shop',
      mall_shop: 'Mall Shop',
      sco: 'SCO Plot',
      commercial_plot: 'Commercial Plot',
      coworking: 'Co-working Space',
      mixed_use: 'Mixed Use',
    };
    return labels[type] || type;
  };

  const nextImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? property.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-24 max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-96 bg-[rgba(201,168,76,0.2)] mb-8 rounded-xl" />
            <div className="h-8 bg-[rgba(201,168,76,0.2)] w-3/4 mb-4 rounded" />
            <div className="h-4 bg-[rgba(201,168,76,0.2)] w-1/2 rounded" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-24 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-[#2a1e00] mb-4">
            Property Not Found
          </h1>
          <Link to="/properties">
            <Button className="btn-primary">Browse Properties</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('http')) {
      try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/api/uploads')) {
          return `${process.env.REACT_APP_BACKEND_URL}${parsed.pathname}`;
        }
      } catch (e) {}
      return url;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const images = property.images?.length > 0
    ? property.images.map(resolveUrl)
    : ['https://images.unsplash.com/photo-1549851501-a521e1e3fb85?w=1200'];

  return (
    <div className="min-h-screen bg-[#fdf9f0]" data-testid="property-detail-page">
      <Header />

      {/* Breadcrumb */}
      <div className="pt-24 bg-white border-b border-[rgba(201,168,76,0.2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/properties"
            className="inline-flex items-center text-sm text-[#6a5a30] hover:text-[#c9a84c] transition-colors"
            data-testid="back-to-properties"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Properties
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            <div className="relative mb-8 bg-[#2a1e00] rounded-2xl overflow-hidden" data-testid="property-gallery">
              <div className="aspect-video relative overflow-hidden">
                <img
                  src={images[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />

                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {property.is_featured && (
                    <span className="badge-featured">Featured</span>
                  )}
                  {property.is_new_launch && (
                    <span className="badge-new">New Launch</span>
                  )}
                  {property.is_hot_deal && (
                    <span className="badge-hot">Hot Deal</span>
                  )}
                </div>

                {/* Listing Type Badge */}
                <div className="absolute top-4 right-4">
                  <span className={`px-4 py-2 text-sm uppercase tracking-wider font-bold rounded-lg ${
                    property.listing_type === 'lease' 
                      ? 'bg-[#c9a84c] text-white' 
                      : 'bg-[#16A34A] text-white'
                  }`}>
                    For {property.listing_type === 'lease' ? 'Lease' : 'Sale'}
                  </span>
                </div>

                {/* Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors rounded-full"
                      data-testid="prev-image"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors rounded-full"
                      data-testid="next-image"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Dots */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex
                            ? 'bg-[#c9a84c]'
                            : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Property Title & Info */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-[#c9a84c] text-xs uppercase tracking-wider mb-3 font-semibold">
                <Building2 className="w-4 h-4" />
                {getPropertyTypeLabel(property.property_type)}
              </div>

              <h1
                className="text-3xl md:text-4xl font-bold text-[#2a1e00] mb-4"
                data-testid="property-title"
              >
                {property.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-[#6a5a30]">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#c9a84c]" />
                  <span>{property.sector}, Gurgaon</span>
                </div>
                <div className="flex items-center gap-2">
                  <Maximize2 className="w-5 h-5 text-[#c9a84c]" />
                  <span>{formatArea(property.area_sqft)}</span>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="bg-gradient-to-r from-[#2a1e00] to-[#1E293B] p-8 mb-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-2">
                <p className="text-white/60 text-sm uppercase tracking-wider">
                  {property.listing_type === 'lease' ? 'Monthly Lease Value' : 'Investment Value'}
                </p>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ${
                  property.listing_type === 'lease' 
                    ? 'bg-[#c9a84c] text-white' 
                    : 'bg-[#16A34A] text-white'
                }`}>
                  For {property.listing_type === 'lease' ? 'Lease' : 'Sale'}
                </span>
              </div>
              <p
                className="text-3xl md:text-4xl font-bold text-[#d4a828]"
                data-testid="property-price"
              >
                {property.price_on_request
                  ? 'Price on Request'
                  : formatPrice(property.price)}
                {property.listing_type === 'lease' && !property.price_on_request && (
                  <span className="text-xl font-normal">/month</span>
                )}
              </p>
              {!property.price_on_request && property.area_sqft && property.price && (
                <p className="text-white/60 text-sm mt-2">
                  ₹{Math.round(property.price / property.area_sqft).toLocaleString()} per sq.ft.
                  {property.listing_type === 'lease' && '/month'}
                </p>
              )}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="mb-8">
              <TabsList className="w-full justify-start border-b border-[rgba(201,168,76,0.2)] bg-transparent p-0">
                <TabsTrigger
                  value="overview"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#c9a84c] rounded-none px-6 py-3"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="benefits"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#c9a84c] rounded-none px-6 py-3"
                >
                  Benefits
                </TabsTrigger>
                <TabsTrigger
                  value="amenities"
                  className="data-[state=active]:border-b-2 data-[state=active]:border-[#c9a84c] rounded-none px-6 py-3"
                >
                  Amenities
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="pt-6">
                <div className="prose max-w-none">
                  <p
                    className="text-[#6a5a30] leading-relaxed"
                    data-testid="property-description"
                  >
                    {property.description}
                  </p>

                  <div className="grid grid-cols-2 gap-6 mt-8">
                    <div className="bg-white p-6 rounded-xl border border-[rgba(201,168,76,0.2)]">
                      <p className="text-sm text-[#8a7a50] uppercase tracking-wider mb-2">
                        Developer
                      </p>
                      <p className="font-bold text-[#2a1e00]">{property.developer}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-[rgba(201,168,76,0.2)]">
                      <p className="text-sm text-[#8a7a50] uppercase tracking-wider mb-2">
                        Property Type
                      </p>
                      <p className="font-bold text-[#2a1e00]">
                        {getPropertyTypeLabel(property.property_type)}
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-[rgba(201,168,76,0.2)]">
                      <p className="text-sm text-[#8a7a50] uppercase tracking-wider mb-2">
                        Location
                      </p>
                      <p className="font-bold text-[#2a1e00]">{property.sector}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-[rgba(201,168,76,0.2)]">
                      <p className="text-sm text-[#8a7a50] uppercase tracking-wider mb-2">
                        Area
                      </p>
                      <p className="font-bold text-[#2a1e00]">
                        {formatArea(property.area_sqft)}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="benefits" className="pt-6">
                <div className="grid md:grid-cols-2 gap-8">
                  {property.investment_benefits?.length > 0 && (
                    <div>
                      <h4 className="font-bold text-[#2a1e00] mb-4">
                        Investment Benefits
                      </h4>
                      <ul className="space-y-3">
                        {property.investment_benefits.map((benefit) => (
                          <li
                            key={benefit}
                            className="flex items-start gap-3 text-[#6a5a30]"
                          >
                            <Check className="w-5 h-5 text-[#c9a84c] flex-shrink-0 mt-0.5" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {property.leasing_benefits?.length > 0 && (
                    <div>
                      <h4 className="font-bold text-[#2a1e00] mb-4">
                        Leasing Benefits
                      </h4>
                      <ul className="space-y-3">
                        {property.leasing_benefits.map((benefit) => (
                          <li
                            key={benefit}
                            className="flex items-start gap-3 text-[#6a5a30]"
                          >
                            <Check className="w-5 h-5 text-[#c9a84c] flex-shrink-0 mt-0.5" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="amenities" className="pt-6">
                {property.amenities?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {property.amenities.map((amenity) => (
                      <div
                        key={amenity}
                        className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[rgba(201,168,76,0.2)]"
                      >
                        <Check className="w-5 h-5 text-[#c9a84c]" />
                        <span className="text-[#2a1e00]">{amenity}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#6a5a30]">
                    Contact us for detailed amenity information.
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Enquiry Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              {/* Quick Actions */}
              <div className="bg-white p-6 mb-6 shadow-lg rounded-xl">
                <div className="flex gap-3">
                  <Button
                    className={`flex-1 ${
                      enquiryType === 'get_price' ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setEnquiryType('get_price')}
                    data-testid="get-price-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Get Price
                  </Button>
                  <Button
                    className={`flex-1 ${
                      enquiryType === 'schedule_visit' ? 'btn-primary' : 'btn-secondary'
                    }`}
                    onClick={() => setEnquiryType('schedule_visit')}
                    data-testid="schedule-visit-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Site Visit
                  </Button>
                </div>
              </div>

              {/* Enquiry Form */}
              <EnquiryForm property={property} enquiryType={enquiryType} />

              {/* Contact Info */}
              <div className="bg-[#2a1e00] p-6 mt-6 rounded-xl">
                <p className="text-white/60 text-sm mb-4">Need immediate assistance?</p>
                <a
                  href="tel:+919999999999"
                  className="flex items-center gap-3 text-white hover:text-[#d4a828] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-bold">+91 99999 99999</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
